import React from "react";
import { Link } from "gatsby";

import {
  FaShieldAlt,
  FaCloud,
  FaHeadset,
  FaRocket,
  FaUsersCog,
  FaClipboardCheck,
} from "react-icons/fa";

const services = [
  {
    icon: <FaCloud size={34} />,

    title: "Microsoft 365 Setup, Governance & Migration",

    text:
      "Replace scattered email systems, unmanaged accounts, and insecure file sharing with a secure Microsoft 365 environment designed for collaboration, governance, and operational control.",

    items: [
      "Microsoft 365 tenant setup & migration",
      "Exchange Online, Teams, SharePoint & OneDrive",
      "User onboarding, licensing & MFA",
      "Secure file access & collaboration controls",
    ],

    cta: "Request Microsoft 365 Consultation",
    outcome: "Cleaner collaboration, safer sharing, and stronger operational control",
  },

  {
    icon: <FaRocket size={34} />,

    title: "Azure Cloud Modernisation & Managed Support",

    text:
      "Improve operational resilience, scalability, and cloud visibility through secure Azure administration, cloud support, and modern infrastructure management.",

    items: [
      "Azure cloud setup & administration",
      "Managed monitoring & operational support",
      "Backup, recovery & cloud optimisation",
      "Cloud troubleshooting & maintenance",
    ],

    cta: "Explore Azure & Cloud Support",
    outcome: "More resilient cloud operations with clearer ownership and support",
  },

  {
    icon: <FaShieldAlt size={34} />,

    title: "Security, Governance & Compliance Alignment",

    text:
      "Reduce cyber risk and improve governance by implementing practical Microsoft cloud security controls aligned with audit and regulatory expectations.",

    items: [
      "MFA, Conditional Access & Zero Trust controls",
      "Microsoft Defender & security hardening",
      "NIST CSF, Essential Eight & ISO 27001 alignment",
      "PCI DSS & HIPAA security control support",
    ],

    cta: "Improve Security & Audit Readiness",
    outcome: "Reduced security exposure and clearer evidence for stakeholders",
  },

  {
    icon: <FaUsersCog size={34} />,

    title: "Identity Protection & Access Governance",

    text:
      "Strengthen accountability and reduce unauthorised access through identity governance, role-based permissions, onboarding/offboarding controls, and secure user management.",

    items: [
      "Entra ID identity governance",
      "Role-based access control (RBAC)",
      "Least-privilege access implementation",
      "User lifecycle & access management",
    ],

    cta: "Secure Identity & Access",
    outcome: "Better control over who can access systems, data, and admin privileges",
  },

  {
    icon: <FaClipboardCheck size={34} />,

    title: "Microsoft 365 Health Check & Transformation Roadmap",

    text:
      "Give leadership a clear view of tenant health, licensing, productivity, security, collaboration, governance, and the best next steps for Microsoft cloud improvement.",

    items: [
      "Tenant health, licensing & productivity review",
      "SharePoint, Teams, OneDrive & workflow opportunities",
      "Security, identity & governance priorities",
      "Plain-English transformation roadmap",
    ],

    cta: "Book Microsoft 365 Health Check",
    outcome: "A practical roadmap to modernise, optimise, secure, and govern Microsoft cloud",
  },

  {
    icon: <FaHeadset size={34} />,

    title: "Managed IT Support & Operational Assistance",

    text:
      "Reduce downtime and unsupported technology issues with reliable remote and on-site IT support, maintenance, troubleshooting, and operational guidance.",

    items: [
      "Remote & on-site support",
      "System troubleshooting & issue response",
      "Patch management & maintenance",
      "Ongoing operational support",
    ],

    cta: "Request Managed IT Support",
    outcome: "Less recurring disruption and clearer day-to-day technology ownership",
  },
];

const ServicesOne = () => {
  return (
    <>
      {/* ================= Main Services ================= */}

      <section className="services-area ptb-100">

        <div className="container">

          {/* ================= Header ================= */}

          <div className="section-title services-section-title">

            <span className="sub-title">
              Our Services
            </span>

            <h2>
              Microsoft Cloud, Digital Workplace, Security & Governance Services
            </h2>

            <p>
              CloudLogic helps organisations implement Microsoft 365, migrate
              from legacy systems, build SharePoint and Teams solutions,
              automate workflows, secure identity and data, improve governance,
              and prepare for audit or customer assurance requirements.
            </p>

          </div>

          {/* ================= Service Cards ================= */}

          <div className="row">

            {services.map((service, index) => (
              <div className="col-lg-4 col-md-6" key={index}>

                <div className="single-service-card">

                  <div className="service-icon">
                    {service.icon}
                  </div>

                  <h3>{service.title}</h3>

                  <p>{service.text}</p>

                  <div className="service-outcome">
                    <span>Business outcome</span>
                    <strong>{service.outcome}</strong>
                  </div>

                  <ul className="service-list">

                    {service.items.map((item, itemIndex) => (
                      <li key={itemIndex}>
                        {item}
                      </li>
                    ))}

                  </ul>

                  <Link to="/contact" className="service-btn">
                    {service.cta}
                  </Link>

                </div>

              </div>
            ))}

          </div>

        </div>

      </section>

      {/* ================= Service Catalogue ================= */}

      <section className="service-catalogue-area pb-100">

        <div className="container">

          <div className="section-title">

            <span className="sub-title">
              Service Catalogue & Pricing
            </span>

            <h2>
              Tailored Microsoft Cloud & Governance Solutions
            </h2>

            <p>
              Every organisation has different operational risks, compliance
              requirements, cloud environments, user structures, and support
              needs. Our recommendations and pricing are tailored based on your
              Microsoft 365 setup, governance priorities, audit expectations,
              migration scope, licensing requirements, and operational goals.
            </p>

          </div>

          <div className="catalogue-content text-center">

            <div className="catalogue-points">
              <span>Microsoft 365 & Azure Governance</span>
              <span>Identity & Security Controls</span>
              <span>Audit & Compliance Readiness</span>
              <span>Managed IT Support</span>
            </div>

            <p className="catalogue-note">
              Start with a Microsoft 365 Health Check to understand your tenant,
              licensing, collaboration, productivity, security, governance, and
              compliance readiness before you invest in broader improvement.
            </p>

            <div className="catalogue-btn">

              <Link to="/contact" className="default-btn">
                Book Microsoft 365 Health Check
              </Link>

            </div>

          </div>

        </div>

      </section>
    </>
  );
};

export default ServicesOne;
